import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, X, Package, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditShizai } from '@/auth/AuthContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { FormSection } from '@/components/enterprise/FormSection';
import LookupCombobox from '@/components/ui/lookup-combobox';
import { shizaiApi } from './shizaiApi';

const NONE = '__none__';
const newLineKey = () => Math.random().toString(36).slice(2);
const emptyLine = () => ({
  _key: newLineKey(),
  seibang: '', unit_no: '', part_no: '', drawing_no: '', name: '', material: '',
  maker: '', qty: 0, uom: '個', item_id: '',
});
const emptyForm = () => ({
  lines: [emptyLine()],
  needed_by: '', department_id: '', priority: 'normal', order_type: 'purchase',
  subcontractor_id: '', notes: '',
});

/**
 * 新規依頼作成 / 依頼編集 — dedicated page (Jun 2026).
 * Replaces the former max-w-3xl scrolling dialog inside 依頼一覧 so the list
 * screen has exactly one job (browse) and this screen has exactly one job
 * (create/update a request). Same API, same payload — presentation only.
 */
export default function RequestFormPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;
  const { user } = useAuth();
  const canWrite = canEditShizai(user);

  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [reqNo, setReqNo] = useState('');

  useEffect(() => { shizaiApi.items().then(setItems).catch(() => {}); }, []);
  useEffect(() => { shizaiApi.departments().then(setDepartments).catch(() => {}); }, []);

  useEffect(() => {
    if (!editing) return;
    setLoading(true);
    shizaiApi.request(id)
      .then((r) => {
        setReqNo(r.request_no || '');
        const lines = (r.lines && r.lines.length) ? r.lines : [{
          seibang: r.seibang || '', unit_no: r.unit_no || '', part_no: r.part_no || '',
          drawing_no: r.drawing_no || '', name: r.name || '', material: r.material || '',
          maker: r.maker || '', qty: r.qty || 0, uom: r.uom || '個', item_id: r.item_id || '',
        }];
        setForm({
          lines: lines.map((l) => ({
            ...emptyLine(),
            // Normalise nulls from the API — controlled inputs must never get null.
            ...Object.fromEntries(Object.entries(l).map(([k, v]) => [k, v == null ? '' : v])),
            qty: l.qty ?? 0,
            uom: l.uom || '個',
            item_id: l.item_id || '',
          })),
          needed_by: r.needed_by || '', department_id: r.department_id || '',
          priority: r.priority || 'normal', order_type: r.order_type || 'purchase',
          subcontractor_id: r.subcontractor_id || '', notes: r.notes || '',
        });
      })
      .catch(() => toast.error(t('shizai.error')))
      .finally(() => setLoading(false));
  }, [editing, id, t]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setForm((p) => ({ ...p, lines: p.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  const removeLine = (i) => setForm((p) => ({ ...p, lines: p.lines.length === 1 ? p.lines : p.lines.filter((_, idx) => idx !== i) }));

  const save = useCallback(async () => {
    const validLines = form.lines.filter((l) => (l.name || '').trim() || (l.part_no || '').trim());
    if (!validLines.length) { toast.error('品名または品番を入力してください'); return; }
    setBusy(true);
    try {
      const body = {
        lines: validLines.map((l) => ({
          seibang: l.seibang || null, unit_no: l.unit_no || null, part_no: l.part_no || null,
          drawing_no: l.drawing_no || null, name: l.name || null, material: l.material || null,
          maker: l.maker || null,
          qty: Number(l.qty) || 0, uom: l.uom || '個', item_id: l.item_id || null,
        })),
        needed_by: form.needed_by || null,
        department_id: form.department_id || null,
        priority: form.priority || 'normal',
        order_type: form.order_type || 'purchase',
        subcontractor_id: form.subcontractor_id || null,
        notes: form.notes || null,
      };
      if (editing) await shizaiApi.updateRequest(id, body);
      else await shizaiApi.createRequest(body);
      toast.success(t('shizai.saved'));
      navigate('/shizai/requests');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('shizai.error'));
    } finally { setBusy(false); }
  }, [form, editing, id, navigate, t]);

  if (!canWrite) {
    return <p className="text-sm text-zinc-500" data-testid="request-form-denied">この操作の権限がありません。</p>;
  }

  return (
    <div className="space-y-6 page-enter pb-24" data-testid="shizai-request-form-page">
      <PageHeader
        testid="request-form-header"
        title={editing ? '依頼を編集' : '新規依頼作成'}
        description={editing
          ? `${reqNo || ''} の内容を更新します`
          : '必要な部材を明細ごとに登録します。保存すると発注ワークフローに載ります。'}
      />

      {loading ? (
        <p className="text-sm text-zinc-400" data-testid="request-form-loading">読み込み中…</p>
      ) : (
        <div className="max-w-3xl space-y-8">
          <FormSection title="基本情報" description="部署・希望納期・優先度" testid="request-form-basic">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.department')}</Label>
                <Select value={form.department_id || NONE} onValueChange={(v) => setField('department_id', v === NONE ? '' : v)}>
                  <SelectTrigger data-testid="request-dept-select" className="h-10 mt-1"><SelectValue placeholder={t('shizai.req.deptNone')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('shizai.req.deptNone')}</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.neededBy')}</Label>
                <DateField value={form.needed_by || ''} onChange={(v) => setField('needed_by', v)} className="mt-1" inputClassName="h-10" warnNonWorking testId="request-needed-by" />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.priority')}</Label>
                <Select value={form.priority} onValueChange={(v) => setField('priority', v)}>
                  <SelectTrigger data-testid="request-priority-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{['low', 'normal', 'high'].map((p) => <SelectItem key={p} value={p}>{t(`shizai.priority.${p}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </FormSection>

          <FormSection
            title={`明細（${form.lines.length}件）`}
            description="品名または品番のいずれかが入力された明細のみ登録されます"
            testid="request-form-lines"
          >
            <div className="space-y-3">
              {form.lines.map((line, i) => (
                <div key={line._key} data-testid={`request-line-${i}`} className="rounded-lg border border-zinc-200 bg-white p-3 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                      <Package size={13} weight="duotone" className="text-zinc-400" /> 明細 #{i + 1}
                    </span>
                    {form.lines.length > 1 && (
                      <button type="button" data-testid={`request-line-remove-${i}`} onClick={() => removeLine(i)}
                        className="text-zinc-400 hover:text-rose-600 transition-colors" title="この明細を削除" aria-label="この明細を削除">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.name')}</Label>
                    <Input data-testid={`request-name-input-${i}`} value={line.name} onChange={(e) => setLine(i, 'name', e.target.value)} className="h-10 mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.seibang')}</Label>
                      <Input value={line.seibang} onChange={(e) => setLine(i, 'seibang', e.target.value)} className="h-10 mt-1 font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.unitNo')}</Label>
                      <Input value={line.unit_no} onChange={(e) => setLine(i, 'unit_no', e.target.value)} className="h-10 mt-1 font-mono" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.partNo')}</Label>
                      <Input data-testid={`request-partno-input-${i}`} value={line.part_no} onChange={(e) => setLine(i, 'part_no', e.target.value)} className="h-10 mt-1 font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.drawingNo')}</Label>
                      <Input data-testid={`request-drawingno-input-${i}`} value={line.drawing_no || ''} onChange={(e) => setLine(i, 'drawing_no', e.target.value)} className="h-10 mt-1 font-mono" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.material')}</Label>
                      <LookupCombobox kind="materials" value={line.material || ''} onChange={(v) => setLine(i, 'material', v)} testId={`request-material-input-${i}`} inputClassName="mt-1 h-10" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.maker')}</Label>
                      <LookupCombobox kind="makers" value={line.maker || ''} onChange={(v) => setLine(i, 'maker', v)} testId={`request-maker-input-${i}`} inputClassName="mt-1 h-10" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.qty')}</Label>
                      <Input type="number" data-testid={`request-qty-input-${i}`} value={line.qty} onChange={(e) => setLine(i, 'qty', e.target.value)} className="h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.uom')}</Label>
                      <LookupCombobox kind="uoms" value={line.uom || ''} onChange={(v) => setLine(i, 'uom', v)} testId={`request-uom-input-${i}`} inputClassName="mt-1 h-10" />
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.item')}</Label>
                      <Select value={line.item_id || NONE} onValueChange={(v) => {
                        const iid = v === NONE ? '' : v;
                        const it = items.find((x) => x.id === iid);
                        // Auto-fill blank fields from the picked item master. Never overwrite typed values.
                        setForm((p) => ({ ...p, lines: p.lines.map((ln, idx) => idx === i ? {
                          ...ln, item_id: iid,
                          name: ln.name || (it?.name || ''),
                          part_no: ln.part_no || (it?.sku || ''),
                          drawing_no: ln.drawing_no || (it?.drawing_no || ''),
                          maker: ln.maker || (it?.maker || ''),
                          uom: ln.uom || (it?.uom || '個'),
                        } : ln) }));
                      }}>
                        <SelectTrigger className="h-10 mt-1" data-testid={`request-item-select-${i}`}><SelectValue placeholder={t('shizai.none')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>{t('shizai.none')}</SelectItem>
                          {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" data-testid="request-line-add" onClick={addLine} className="h-10 w-full">
                <Plus size={15} weight="bold" className="mr-1.5" /> 明細を追加
              </Button>
            </div>
          </FormSection>

          <FormSection title="備考" testid="request-form-notes">
            <div>
              <Label className="text-xs font-medium text-zinc-600">{t('shizai.req.notes')}</Label>
              <Input value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="request-notes-input" className="h-10 mt-1" />
            </div>
          </FormSection>
        </div>
      )}

      {/* Sticky save bar — a long form must never hide its primary action */}
      {!loading && (
        <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-zinc-200 bg-white/95 backdrop-blur px-4 py-3 flex items-center justify-end gap-2" data-testid="request-form-sticky-bar">
          <Button variant="outline" onClick={() => navigate('/shizai/requests')} data-testid="request-form-cancel-sticky" className="h-10">
            {t('shizai.cancel')}
          </Button>
          <Button onClick={save} disabled={busy} data-testid="request-save-btn-sticky" className="h-10">
            <FloppyDisk size={17} weight="duotone" className="mr-1.5" />
            {busy ? t('shizai.saving') : (editing ? t('shizai.save') : '依頼を作成')}
          </Button>
        </div>
      )}
    </div>
  );
}
